<i18n>
{
  "fr": {
    "configuratorLayerEditorModal": {
      "title": {
        "addImage": "Insérez une image",
        "editText": "Modifier mon texte",
        "cropImage": "Modifier l'image"
      }
    }
  }
}
</i18n>

<template>
  <MountingPortal mount-to="#portal-container--footer" :name="name" append>
    <BaseModal
      v-model="localIsActive"
      full-screen-until-xs
      class="configurator-layer-editor-modal"
      :title="currentView.title"
    >
      <template #body>
        <component
          :is="currentView.component"
          v-bind="props"
          v-on="listeners"
        />
      </template>
    </BaseModal>
  </MountingPortal>
</template>

<script>
import BaseModal from '../../BaseModal';
import ConfiguratorLayerEditorModalImageImport from './ConfiguratorLayerEditorModal/ConfiguratorLayerEditorModalImageImport';
import ConfiguratorLayerEditorModalEdit from './ConfiguratorLayerEditorModal/ConfiguratorLayerEditorModalEdit';
import ConfiguratorLayerEditorModalAiGenImage from './ConfiguratorLayerEditorModal/ConfiguratorLayerEditorModalAiGenImage';
import { randomStr } from '../../../utils/String';
import { delay } from '../../../utils/TimeUtils';
import { ZoneType } from '../../../api/static/zones';
import { filterProps } from '../../../utils/ObjectUtils';
import ConfiguratorLayerEditorModalCropImage from './ConfiguratorLayerEditorModal/ConfiguratorLayerEditorModalCropImage';
import HistoryBackClosable from '../../../mixins/HistoryBackClosable';
import EscapeClosable from '../../../mixins/EscapeClosable';
import { Breakpoint } from '../../../utils/breakpoints';

const ImportStep = Object.freeze({
  IMAGE_IMPORT_STEP_1: 0,
  IMAGE_IMPORT_STEP_2: 2,
  TEXT_IMPORT: 3,
  IMAGE_IMPORT_STEP_2_CROP: 4,
  AI_GEN_IMAGE_IMPORT: 5,
});

export default {
  name: 'ConfiguratorLayerEditorModal',
  components: { BaseModal, ConfiguratorLayerEditorModalAiGenImage },
  mixins: [HistoryBackClosable, EscapeClosable],
  props: {
    active: Boolean,
    inputImageUrl: {
      type: String,
      default: undefined,
    },
    group: {
      type: Object,
      required: true,
    },
    customization: {
      type: Object,
      required: true,
    },
    color: {
      type: String,
      default: undefined,
    },
    customProduct: Boolean,
    screenPrinting: Boolean,
  },
  data() {
    return {
      name: `configurator-layer-editor-modal--${randomStr()}`,
      file: null,
      activeView: undefined,
      componentMap: Object.freeze({
        [ImportStep.IMAGE_IMPORT_STEP_1]: {
          title: this.$t('configuratorLayerEditorModal.title.addImage'),
          component: ConfiguratorLayerEditorModalImageImport,
        },
        [ImportStep.IMAGE_IMPORT_STEP_2]: {
          title: this.$t('configuratorLayerEditorModal.title.addImage'),
          component: ConfiguratorLayerEditorModalEdit,
        },
        [ImportStep.TEXT_IMPORT]: {
          title: this.$t('configuratorLayerEditorModal.title.editText'),
          component: ConfiguratorLayerEditorModalEdit,
        },
        [ImportStep.IMAGE_IMPORT_STEP_2_CROP]: {
          title: this.$t('configuratorLayerEditorModal.title.cropImage'),
          component: ConfiguratorLayerEditorModalCropImage,
        },
        [ImportStep.AI_GEN_IMAGE_IMPORT]: {
          title: this.$t('configuratorLayerEditorModal.title.addImage'),
          component: ConfiguratorLayerEditorModalAiGenImage,
        },
      }),
      /** @type {ZoneCustomizationCustomConfigImage|ZoneCustomizationCustomConfigText|undefined} */
      localCustomization: undefined,
    };
  },
  computed: {
    isLargeScreen() {
      return this.$matchMedia[Breakpoint.L];
    },
    localIsActive: {
      get() {
        return this.active;
      },
      set(v) {
        this.$emit('update:active', v);
      },
    },
    currentView() {
      return this.componentMap[this.activeView];
    },
    props() {
      const props = {};

      switch (this.activeView) {
        case ImportStep.IMAGE_IMPORT_STEP_1: {
          props.screenPrinting = this.screenPrinting;
          props.zone = this.zone;

          break;
        }

        case ImportStep.IMAGE_IMPORT_STEP_2:
        case ImportStep.TEXT_IMPORT:
        case ImportStep.IMAGE_IMPORT_STEP_2_CROP: {
          props.group = this.group;
          props.customization = this.localCustomization;
          props.color = this.color;

          break;
        }
        case ImportStep.AI_GEN_IMAGE_IMPORT: {
          props.initialImage = this.file;
          break;
        }
      }

      return props;
    },
    listeners() {
      const listeners = {};

      switch (this.activeView) {
        case ImportStep.IMAGE_IMPORT_STEP_1: {
          listeners.change = this.setFile;
          listeners.click = this.displayAiGenImageView;
          break;
        }

        case ImportStep.IMAGE_IMPORT_STEP_2: {
          listeners['go-back'] = this.goToStep1;
          listeners['go-to-ai-editing'] = this.displayAiGenImageView;
          listeners.submit = this.onSubmit;

          break;
        }

        case ImportStep.TEXT_IMPORT: {
          listeners['reset-text'] = this.resetText;
          listeners.submit = this.onSubmit;

          break;
        }

        case ImportStep.IMAGE_IMPORT_STEP_2_CROP: {
          listeners['go-back'] = this.goToStep1;
          listeners.submit = this.onCropSubmit;

          break;
        }

        case ImportStep.AI_GEN_IMAGE_IMPORT: {
          listeners['select-image'] = this.setFile;
          break;
        }
      }

      return listeners;
    },
    /**
     *
     * @return {Zone}
     */
    zone() {
      return this.group.zoneMap[this.customization.templateZoneId];
    },
  },
  watch: {
    active(isActive) {
      if (isActive) {
        this.resetActiveView();

        if (this.isLargeScreen) {
          this.openEscapeClosable();
        } else {
          this.openWithHistory();
        }
      } else {
        this.closeEscapeClosable();
        this.closeWithHistory();
      }
    },
    inputImageUrl: {
      handler(url) {
        this.setUrl(url);
      },
      immediate: true,
    },
    customization: {
      handler(customization) {
        this.localCustomization = customization;
      },
      immediate: true,
    },
  },
  created() {
    this.resetActiveView();
  },
  methods: {
    resetActiveView() {
      if (this.zone.type === ZoneType.IMAGE) {
        this.activeView =
          this.customProduct && this.inputImageUrl
            ? ImportStep.IMAGE_IMPORT_STEP_2
            : ImportStep.IMAGE_IMPORT_STEP_1;
      } else if (this.zone.type === ZoneType.TEXT) {
        this.activeView = ImportStep.TEXT_IMPORT;
      } else {
        throw new Error('Invalid zone type');
      }
    },
    onHistoryBackClosableClosed() {
      this.localIsActive = false;
    },
    onEscapeClosableClosed() {
      this.localIsActive = false;
    },
    createBlankCustomizationFromActiveCustomization() {
      return filterProps(this.customization, ['id', 'templateZoneId', 'type']);
    },
    setFile(file) {
      this.file = file;

      // Reset customization on file change
      this.localCustomization =
        this.createBlankCustomizationFromActiveCustomization();

      this.setUrl(URL.createObjectURL(file));

      if (this.customProduct) {
        this.activeView = ImportStep.IMAGE_IMPORT_STEP_2;
      } else {
        this.activeView = ImportStep.IMAGE_IMPORT_STEP_2_CROP;
      }
    },
    setUrl(url) {
      this.localCustomization = {
        ...this.localCustomization,
        url,
      };
    },
    displayAiGenImageView() {
      this.activeView = ImportStep.AI_GEN_IMAGE_IMPORT;
    },
    goToStep1() {
      this.activeView = ImportStep.IMAGE_IMPORT_STEP_1;
    },
    async onSubmit(zoneCustomization) {
      this.localCustomization = zoneCustomization;

      // TODO Validate file density

      this.$emit('update:file', this.file);
      this.$emit('update:customization', this.localCustomization);

      window.dispatchEvent(
        new CustomEvent('lgf.configurator.customizationUpdate', {
          detail: {
            content_type: this.customization.type,
          },
        })
      );

      // Waiting for the next event loop to hide modal (avoids render error during modal fade out)
      await delay(0);

      this.localIsActive = false;
    },
    async onCropSubmit(imageBlob) {
      this.$emit('update:file', imageBlob);
      this.$emit('update:customization', this.localCustomization);

      // Waiting for the next event loop to hide modal (avoids render error during modal fade out)
      await delay(0);

      this.localIsActive = false;
    },
    async resetText() {
      this.localCustomization =
        this.createBlankCustomizationFromActiveCustomization();

      this.$emit('update:customization', this.localCustomization);

      // Waiting for the next event loop to hide modal (avoids render error during modal fade out)
      await delay(0);

      this.localIsActive = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-layer-editor-modal {
  &::v-deep {
    .base-modal__body {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .base-modal__container {
      @include breakpoint($breakpoint-xs) {
        width: $breakpoint-xs;
        max-height: $breakpoint-l;
        height: 100%;
      }
    }
  }
}
</style>
